import { Api } from "@/api"
import type { CreateShipmentData, UploadShipmentFilesData } from "@/api/shipments"
import type {
	DocumentUpload,
	Id,
	SearchShipmentsResult,
	ShipmentLog,
	ShipmentLogDocumentKey,
	TFSEntryMetadata,
} from "@/models"
import type { PlainDate } from "@/temporal"
import { inject, provide, type InjectionKey } from "vue"
import { type Loadable } from "vue-utils"
import { useSearchShipments } from "./useSearchShipments"

/* 
	Shipment store is defined using Vue's dependency injection (rather than via Pinia like other stores)
	due to the data being dependant on the visited page URL
*/

const injectionKey = Symbol("shipments-store") as InjectionKey<ShipmentsStore>

export function useShipmentsStore(): ShipmentsStore {
	const store = inject(injectionKey)
	if (!store) {
		throw new Error("Shipments store not defined")
	}
	return store
}

export function setupShipmentsStore(tfs: TFSEntryMetadata): ShipmentsStore {
	const store = new ShipmentsStore(tfs)
	provide(injectionKey, store)

	return store
}

class ShipmentsStore {
	private readonly loadedTfs: TFSEntryMetadata
	private readonly search

	constructor(loadedTfs: TFSEntryMetadata) {
		this.loadedTfs = loadedTfs
		this.search = useSearchShipments(loadedTfs.id)
	}

	/**
      The TFS metadata also comes through from the shipment log table.

      When the table is refreshed (i.e. after creating documents or a new shipment log), the displayed tfs metadata
      also will likely need to update. 
   */
	get tfs(): TFSEntryMetadata {
		if ("result" in this.shipmentsRef && this.shipmentsRef.result) {
			return this.shipmentsRef.result.tfsMetadata
		}
		return this.loadedTfs
	}

	get requestOptions() {
		return this.search.requestOptions
	}

	get shipmentsRef(): Loadable<SearchShipmentsResult> {
		return this.search.loadableRef
	}

	async refreshResults(): Promise<void> {
		await this.search.refreshResults()
	}

	resetSearchOptions(): void {
		this.search.resetOptions()
	}

	hasResults(): boolean {
		return this.search.hasResults()
	}

	getResults(): SearchShipmentsResult {
		return this.search.getResults()
	}

	async createShipment(data: Omit<CreateShipmentData, "tfsId">): Promise<ShipmentLog> {
		const shipment = await Api.shipments.create({
			...data,
			tfsId: this.tfs.id,
		})
		void this.refreshResults()
		return shipment
	}

	async uploadDocuments(data: UploadShipmentFilesData) {
		await Api.shipments.uploadDocuments(data)
		void this.refreshResults()
	}

	async deleteDocument(document: DocumentUpload, type: ShipmentLogDocumentKey) {
		await Api.shipments.deleteDocument(type, document.guid)
		void this.refreshResults()
	}

	async editShipmentDate(shipment: Id | ShipmentLog, date: PlainDate) {
		const shipmentId = typeof shipment === "number" ? shipment : shipment.id
		await Api.shipments.setShipmentDate(shipmentId, date)
		void this.refreshResults()
	}

	async cancelShipment(shipment: Id | ShipmentLog) {
		const shipmentId = typeof shipment === "number" ? shipment : shipment.id
		await Api.shipments.cancel(shipmentId)
		void this.refreshResults()
	}

	async completeShipment(shipment: Id | ShipmentLog) {
		const shipmentId = typeof shipment === "number" ? shipment : shipment.id
		await Api.shipments.complete(shipmentId)
		void this.refreshResults()
	}

	async deleteShipment(shipment: Id | ShipmentLog) {
		const shipmentId = typeof shipment === "number" ? shipment : shipment.id
		await Api.shipments.delete(shipmentId)
		void this.refreshResults()
	}

	async getNextShipmentNumber() {
		return await Api.shipments.getNextNumber(this.tfs.id)
	}
}
